import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Star, Quote, Loader2, ShieldCheck, MessageSquarePlus } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { api } from "../lib/api";
import { formatApiError } from "../context/AuthContext";

const Stars = ({ value, onChange, testPrefix }) => (
  <div className="flex gap-1">
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        data-testid={onChange ? `${testPrefix}-star-${n}` : undefined}
        onClick={onChange ? () => onChange(n) : undefined}
        className={onChange ? "transition-transform duration-150 hover:scale-125" : "cursor-default"}
        aria-label={`${n} stelle`}
      >
        <Star className="w-5 h-5" style={{ color: n <= value ? "#FFE600" : "#3a3d4d", fill: n <= value ? "#FFE600" : "transparent" }} />
      </button>
    ))}
  </div>
);

export default function FeedbackPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [rating, setRating] = useState(5);
  const [message, setMessage] = useState("");
  const [consent, setConsent] = useState(false);
  const [privacyText, setPrivacyText] = useState("");
  const [sending, setSending] = useState(false);

  const load = () => api.get("/feedback").then((r) => setItems(r.data)).finally(() => setLoading(false));

  useEffect(() => {
    load();
    api.get("/settings").then((r) => setPrivacyText(r.data.texts?.privacy_text || "")).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!name || !message) { toast.error("Inserisci nome e recensione."); return; }
    if (!consent) { toast.error("Devi accettare l'informativa sulla privacy."); return; }
    setSending(true);
    try {
      await api.post("/feedback", { name, rating, message, privacy_consent: consent });
      toast.success("Grazie! La tua recensione sarà pubblicata dopo l'approvazione.");
      setName(""); setRating(5); setMessage(""); setConsent(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Errore nell'invio.");
    } finally { setSending(false); }
  };

  return (
    <div className="noise-overlay min-h-screen">
      <Navbar />
      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-16">
        <span className="font-mono text-xs uppercase tracking-widest" style={{ color: "#FFE600" }}>Dicono di me</span>
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter text-white mt-2">
          Recensioni <span className="text-neon-pink drop-shadow-[0_0_14px_rgba(255,0,127,0.5)]">clienti</span>
        </h1>

        <div className="grid lg:grid-cols-5 gap-8 mt-10">
          <div className="lg:col-span-3 space-y-4">
            {loading ? (
              <p className="font-mono text-neon-cyan animate-pulse-glow">Caricamento…</p>
            ) : items.length === 0 ? (
              <div data-testid="feedback-empty" className="flex flex-col items-center py-16 text-muted-foreground border border-white/10 rounded-sm bg-card">
                <Quote className="w-12 h-12 mb-3 opacity-40" /><p className="font-light">Ancora nessuna recensione pubblicata.</p>
              </div>
            ) : (
              items.map((f, i) => (
                <motion.div key={f.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
                  data-testid={`feedback-item-${i}`}
                  className="p-6 bg-card border border-white/10 rounded-sm hover:border-neon-pink/40 transition-colors duration-300">
                  <Stars value={f.rating} />
                  <p className="text-sm text-foreground font-light mt-3 leading-relaxed">"{f.message}"</p>
                  <p className="font-display font-bold text-white mt-3">— {f.name}</p>
                </motion.div>
              ))
            )}
          </div>

          <div className="lg:col-span-2">
            <form onSubmit={submit} className="bg-card border border-white/10 rounded-sm p-6 sticky top-24 space-y-4">
              <h2 className="font-display font-bold text-lg tracking-tight text-white flex items-center gap-2"><MessageSquarePlus className="w-5 h-5 text-neon-cyan" /> Lascia una recensione</h2>
              <Input data-testid="feedback-name" placeholder="Il tuo nome" value={name} onChange={(e) => setName(e.target.value)} className="bg-input border-white/10 text-white rounded-sm" />
              <div>
                <span className="text-sm text-muted-foreground block mb-2">Valutazione</span>
                <Stars value={rating} onChange={setRating} testPrefix="feedback" />
              </div>
              <Textarea data-testid="feedback-message" placeholder="Raccontami la tua esperienza…" rows={4} value={message} onChange={(e) => setMessage(e.target.value)} className="bg-input border-white/10 text-white rounded-sm resize-none" />
              <div className="flex gap-3 items-start p-3 bg-input/50 border border-white/10 rounded-sm">
                <Checkbox data-testid="feedback-consent" id="fb-consent" checked={consent} onCheckedChange={(v) => setConsent(!!v)} className="mt-0.5 border-neon-cyan data-[state=checked]:bg-neon-cyan data-[state=checked]:text-black" />
                <label htmlFor="fb-consent" className="text-[11px] text-muted-foreground font-light leading-relaxed cursor-pointer">
                  <span className="flex items-center gap-1.5 text-neon-cyan font-mono uppercase tracking-wider text-[10px] mb-1"><ShieldCheck className="w-3 h-3" /> Consenso privacy</span>
                  {privacyText || "Acconsento al trattamento dei dati ai sensi del Reg. (UE) 2016/679 (GDPR) e del D.Lgs. 196/2003."}
                </label>
              </div>
              <Button data-testid="feedback-submit" type="submit" disabled={sending || !consent} className="w-full h-11 bg-neon-pink text-white hover:bg-white hover:text-black rounded-sm font-semibold gap-2 transition-colors duration-200 disabled:opacity-50">
                {sending ? <><Loader2 className="w-4 h-4 animate-spin" /> Invio…</> : "Invia recensione"}
              </Button>
              <p className="text-[11px] text-muted-foreground font-light text-center">Le recensioni sono pubblicate dopo l'approvazione dell'amministratore.</p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
